
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Progress } from '../../components/ui/progress';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import {
    Clock,
    CheckCircle2,
    AlertCircle,
    Loader2,
    ChevronLeft,
    ChevronRight,
    Flag,
    BookOpen,
    Calendar
} from 'lucide-react';
import RichText from '../../components/RichText';
import CachedImage from '../../components/CachedImage';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ClassicChallenge = () => {
    const navigate = useNavigate();
    const { isAuthenticated } = useAuth();

    const [step, setStep] = useState('check');
    const [challenge, setChallenge] = useState(null);
    const [currentQuestion, setCurrentQuestion] = useState(0);
    const [answers, setAnswers] = useState({});
    const [timeLeft, setTimeLeft] = useState(3600); // 1 hour
    const [loading, setLoading] = useState(true);
    const [result, setResult] = useState(null);
    const [paperStatus, setPaperStatus] = useState(null);

    const handleSubmit = useCallback(async () => {
        if (!challenge) return;

        const answersArray = challenge.questions.map(q => ({
            question_id: q.id,
            selected_option: answers[q.id] ?? -1
        }));

        setLoading(true);
        try {
            const response = await axios.post(`${API}/challenges/submit`, {
                challenge_id: challenge.challenge_id,
                answers: answersArray,
                time_taken_seconds: (challenge.time_limit_seconds || 3600) - timeLeft
            });
            setResult(response.data);
            setStep('result');
            toast.success('Paper submitted!');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to submit');
        } finally {
            setLoading(false);
        }
    }, [challenge, answers, timeLeft]);

    useEffect(() => {
        let timer;
        if (step === 'quiz' && timeLeft > 0) {
            timer = setInterval(() => {
                setTimeLeft(prev => {
                    if (prev <= 1) {
                        clearInterval(timer);
                        handleSubmit();
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
        }
        return () => clearInterval(timer);
    }, [step, timeLeft, handleSubmit]);

    const checkPaperStatus = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${API}/daily-paper/status`);
            setPaperStatus(response.data);
            if (response.data.already_completed) {
                setResult(response.data.attempt);
                setStep('completed');
            } else if (response.data.paper_available) {
                setStep('ready');
            } else {
                setStep('no-paper');
            }
        } catch (error) {
            // Fallback to practice mode if no daily paper system
            setStep('ready');
        } finally {
            setLoading(false);
        }
    };

    const startChallenge = async () => {
        setLoading(true);
        try {
            const response = await axios.post(`${API}/challenges/start`, {
                subject: null
            });
            setChallenge(response.data);
            setStep('quiz');
            setTimeLeft(response.data.time_limit_seconds || 3600);
            toast.success("Today's paper started! You have 1 hour.");
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to start. Try again later.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        checkPaperStatus();
    }, []);

    const formatTime = (seconds) => {
        const hrs = Math.floor(seconds / 3600);
        const mins = Math.floor((seconds % 3600) / 60);
        const secs = seconds % 60;
        if (hrs > 0) {
            return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
        }
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const selectOption = (questionId, optionIndex) => {
        setAnswers(prev => ({
            ...prev,
            [questionId]: optionIndex
        }));
    };

    // Renders...
    if (loading && (step === 'check' || step === 'loading')) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
        );
    }

    if (step === 'completed' && paperStatus?.attempt) {
        // Reuse completed UI
        return (
            <div className="min-h-screen pt-20 pb-12 px-6">
                <div className="container mx-auto max-w-2xl text-center">
                    <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-green-500/10 mb-6">
                        <CheckCircle2 className="w-10 h-10 text-green-500" />
                    </div>
                    <h1 className="text-3xl font-bold mb-4">Today's Paper Completed!</h1>
                    <p className="text-muted-foreground mb-8">
                        You've already completed today's daily paper. Come back tomorrow for a new set of questions.
                    </p>
                    <div className="grid grid-cols-3 gap-4 mb-8">
                        <div className="stat-card text-center">
                            <div className="text-2xl font-bold text-primary">{paperStatus.attempt.score}%</div>
                            <div className="text-sm text-muted-foreground">Score</div>
                        </div>
                        {/* ... other stats */}
                    </div>
                    <Button onClick={() => navigate('/challenge')} variant="outline">Back to Menu</Button>
                </div>
            </div>
        )
    }

    if (step === 'ready') {
        return (
            <div className="min-h-screen pt-20 pb-12 px-6">
                <div className="container mx-auto max-w-2xl">
                    <div className="text-center mb-12">
                        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 text-primary mb-6">
                            <Calendar className="w-4 h-4" />
                            <span className="font-medium">{new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</span>
                        </div>
                        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mb-4">
                            Today's Daily Paper
                        </h1>
                        <Button
                            size="lg"
                            className="btn-hover px-12"
                            onClick={startChallenge}
                            disabled={loading}
                        >
                            Start Today's Paper
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    // Quiz Mode
    if (step === 'quiz' && challenge) {
        const question = challenge.questions[currentQuestion];
        const progress = ((currentQuestion + 1) / challenge.questions.length) * 100;
        const totalQuestions = challenge.questions.length;

        return (
            <div className="min-h-screen pt-20 pb-12 px-4 sm:px-6 lg:px-8">
                <div className="container mx-auto max-w-7xl">
                    {/* Header */}
                    <div className="flex items-center justify-between mb-6">
                        <div className="flex items-center gap-4">
                            <span className="text-sm font-medium text-muted-foreground">
                                Question {currentQuestion + 1} of {totalQuestions}
                            </span>
                        </div>
                        <div className={`flex items-center gap-2 px-4 py-2 rounded-lg bg-secondary`}>
                            <Clock className="w-4 h-4" />
                            <span className="timer-display">
                                {formatTime(timeLeft)}
                            </span>
                        </div>
                    </div>

                    <Progress value={progress} className="h-2 mb-6" />

                    {/* Question */}
                    <div className="bg-card border border-border rounded-xl p-6 sm:p-8 mb-6">
                        <h2 className="text-xl sm:text-2xl font-semibold leading-relaxed">
                            <RichText>{question.text}</RichText>
                        </h2>
                        {question.image_url && (
                            <div className="mt-4">
                                <CachedImage
                                    src={question.image_url}
                                    alt="Question"
                                    className="max-h-96 mx-auto rounded-lg border border-border"
                                />
                            </div>
                        )}
                    </div>

                    {/* Options */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 mb-6">
                        {question.options.map((option, index) => {
                            const optionText = typeof option === 'string' ? option : option.text;
                            const optionImage = typeof option === 'object' ? option.image_url : null;

                            return (
                                <button
                                    key={index}
                                    onClick={() => selectOption(question.id, index)}
                                    className={`option-button ${answers[question.id] === index ? 'selected' : ''
                                        }`}
                                >
                                    <div className="flex items-center gap-4">
                                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center font-semibold text-base
                          ${answers[question.id] === index
                                                ? 'bg-primary text-primary-foreground'
                                                : 'bg-secondary'
                                            }`}>
                                            {String.fromCharCode(65 + index)}
                                        </div>
                                        <div className="flex-1 text-left">
                                            {optionText && <span className="text-base"><RichText>{optionText}</RichText></span>}
                                            {optionImage && <CachedImage src={optionImage} alt={`Option ${index + 1}`} className="mt-2 max-h-32 rounded" />}
                                        </div>
                                    </div>
                                </button>
                            );
                        })}
                    </div>

                    {/* Navigation */}
                    <div className="flex items-center justify-between">
                        <Button
                            variant="outline"
                            onClick={() => setCurrentQuestion(prev => Math.max(0, prev - 1))}
                            disabled={currentQuestion === 0}
                        >
                            <ChevronLeft className="w-4 h-4 mr-2" />
                            Previous
                        </Button>

                        <Button
                            onClick={handleSubmit}
                            disabled={loading}
                            variant="destructive"
                        >
                            <Flag className="w-4 h-4 mr-2" />
                            Submit Paper
                        </Button>

                        <Button
                            onClick={() => setCurrentQuestion(prev => Math.min(totalQuestions - 1, prev + 1))}
                            disabled={currentQuestion === totalQuestions - 1}
                        >
                            Next
                            <ChevronRight className="w-4 h-4 ml-2" />
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    // Result
    if (step === 'result' && result) {
        return (
            <div className="min-h-screen pt-20 pb-12 px-6">
                <div className="container mx-auto max-w-4xl">
                    <h1 className="text-3xl font-bold mb-6">Results</h1>
                    <div className="stat-card mb-6">
                        <div className="text-4xl font-bold text-center text-primary">{result.score}%</div>
                        <p className="text-center text-muted-foreground">Your Score</p>
                    </div>
                    <Button onClick={() => navigate('/challenge')}>Back to Challenges</Button>
                </div>
            </div>
        )
    }

    return <div>Loading...</div>;
};

export default ClassicChallenge;
